import { SCENE_EVENTS, VIEWPORT_EVENTS, SETTINGS_EVENTS } from '../../constants/Events';
import { ButtonSimple } from '../../input/ButtonSimple';
import { GameHudButtonGroup } from './GameHudButtonGroup';
import { GameHudIcons } from './GameHudIcons';

const STATE_EMPTY = { modal: false, buttons: [] };
/**
 *
 * @alias components.plugin.GameHudPlugin
 * @classdesc A GameHud component that displays UI buttons
 *
 * ***This is an optional jgg component, and so will be only be available on request when extending {@link AppBase}.***
 *
 * <pre>
 * components : {
 *  hud : GameHudPlugin
 * }
 * </pre>
 *
 * Then you can access it via the game scope
 *
 * <pre>
 * this.game.hud
 * </pre>
 *
 * The HudConfigObject can be configured within the [getConfig()]{@link getConfigBase} component options
 *
 * <pre>
 * components : {
 *  hud : { component : GameHudPlugin,  data :  { ...HudConfigObject } }
 * }
 * </pre>
 *
 * Or by calling the [setHudConfig]{@link components.core.GameHudPlugin#setHudConfig} method
 *
 * <pre>
 * this.game.hud.setHudConfig( { ...HudConfigObject } )
 * </pre>
 *
 * If you are not using {@link AppBase} and wish to use this component, you can install it as a [Phaser Global Plugin]{@link https://photonstorm.github.io/phaser3-docs/Phaser.Plugins.PluginManager.html}
 * <pre>
 * game.plugins.install('key', GameHudPlugin, true, 'scenekey' || null,  { ...HudConfigObject } );
 * </pre>
 *
 * @see{@link HudConfigObject}
 * @see {@link HudConfigModal}
 * @see {@link HudConfigButtonGroup}
 * @see {@link ButtonSimpleOptions}
 *
 */

class GameHudPlugin extends Phaser.Plugins.BasePlugin {
  constructor(pluginManager) {
    super(pluginManager);

    this._state = false;
    this._hudscene = false;
    this._hudConfig = {};
    this._stateButtons = {};
    this._stateComponents = {};
    this._buttonGroups = {};
    this._savedStates = [];
    this._events = new Phaser.Events.EventEmitter();
  }

  // =============================================================================================
  // SETUP
  // =============================================================================================

  init(config) {
    this.setHudConfig(config);
    this.game.scene.add('Hud', {}, true);
  }

  start() {}

  onGameReady() {
    this.game.viewportController.events.on(VIEWPORT_EVENTS.UPDATED, this._updatePosition, this);
    this.game.sceneController.events.on(SCENE_EVENTS.BRING_TO_TOP, this.bringToTop, this);
    this.game.settings.events.on(SETTINGS_EVENTS.CHANGED, this._handleSettingsChanged, this);
  }

  /**
   * Set the Hud configuration by passing through an object that outlines buttons, layouts and groups
   * @param {HudConfigObject} config The HudConfig to use
   */
  setHudConfig(config) {
    this._hudConfig = config || {};
  }

  /**
   * Bring the hud to the top of the scene stack
   */
  bringToTop() {
    this.scene.scene.bringToTop();
  }

  /**
   * Get the default button class as used by the game hud.
   * @type {*}
   */
  get defaultButton() {
    return this._hudConfig.defaultButton ? this._hudConfig.defaultButton : ButtonSimple;
  }

  // =============================================================================================
  // GETTERS
  // =============================================================================================

  /**
   * Get the scene used by the hud
   * @type Phaser.Scene
   * @readonly
   */
  get scene() {
    if (!this._hudscene) {
      this._hudscene = this.game.scene.getScene('Hud');
    }
    return this._hudscene;
  }

  /**
   * The event emitter for this component.
   * @type {Phaser.Events.EventEmitter}
   */
  get events() {
    return this._events;
  }

  /**
   * Get a particular button in the hud
   * @param {string} id id of button to fetch
   * @returns button if found
   */
  getButton(id) {
    return this._stateButtons[id];
  }

  /**
   * Get a particular component in the hud
   * @param {string} id id of component to fetch
   * @returns component if found
   */
  getComponent(id) {
    return this._stateComponents[id];
  }

  // =============================================================================================
  // HUD STATE
  // =============================================================================================

  /**
   * @description Set the current Hud state by pasing through an ID as configured in the [HudConfigObject]{@link HudConfigObject}
   * @param {string} stateid The ID of the hud state to set
   * @param {bool} [save=true] SHould the current hud state be saved into history
   *
   */
  setState(stateid, save = true) {
    if (!this._hudConfig) {
      console.warn('[HudPlugin] attempting to set state before adding config');
      return;
    }
    if (!stateid) {
      this.scene.scene.bringToTop();
      return;
    }

    if (save && this._state) {
      this._addToHistory(this._state);
    }

    this._clearCurrentState();
    const stateconfig = stateid === 'EMPTY' ? STATE_EMPTY : this._hudConfig.states[stateid];
    if (stateconfig) {
      this._state = { id: stateid };
      this.showModal(stateconfig.modal);
      this._createStateButtons(stateconfig.buttons);
    }
    this._checkHudIcons();
    this.scene.scene.bringToTop();
    this._updatePosition();

    this.events.emit('STATE_CHANGE', stateid);
  }

  /**
   * clear the hud to an empty state
   */
  setEmptyState() {
    this.showModal(false);
    this.setState('EMPTY');
  }

  /**
   * Resume the previous state stored in history
   */
  resumePreviousState() {
    const state = this._retrievePreviousState();
    this.setState(state.id, false);
  }

  /** @private */
  _retrievePreviousState() {
    const state = this._savedStates.pop();
    return state || false;
  }

  /** @private */
  _clearCurrentState() {
    this.showModal(false);
    this._clearStateButtons();
    this._clearStateComponents();
  }

  /**
   * Add a state to the hud not previously set in HudConfig
   * @param {stateid} stateid The id to give the added state
   * @param {config} config The state cofiguration object
   */
  addHudState(stateid, config) {
    if (this._hudConfig.states[stateid]) return;
    this._hudConfig.states[stateid] = config;
  }

  // =============================================================================================
  // HISTORY
  // =============================================================================================

  /** @private */
  _addToHistory(state) {
    if (state) {
      this._savedStates.push(state);
    }
  }

  // =============================================================================================
  // COMPONENTS
  // =============================================================================================

  _createStateComponent(config) {
    if (this._stateComponents[config.id]) return false;
    const component = new config.classname(this.scene, config);
    if (config.position) {
      component.x = config.position.x;
      component.y = config.position.y;
    }
    this._stateComponents[config.id] = component;
    // add button to group if desired
    if (config.buttongroup) {
      this._addToButtonGroup(component, config.buttongroup);
    }
  }

  _getComponentConfig(componentId) {
    return this._hudConfig.components?.find((c) => c.id === componentId);
  }

  /** @private */
  _clearStateComponents() {
    Object.keys(this._stateComponents).forEach((key) => {
      this._stateComponents[key].destroy();
      this._stateComponents[key] = null;
    });
    this._stateComponents = {};
  }

  // =============================================================================================
  // BUTTONS
  // =============================================================================================

  /**
   * Add a button configuation not previously added in hud config
   * @param {object} btnconfig button configuration object to add
   */
  addButtonConfig(btnconfig) {
    if (this._hudConfig.buttons.find((b) => b.id === btnconfig.id)) return;
    this._hudConfig.buttons.push(btnconfig);
  }

  /** @private */
  _createStateButtons(buttons) {
    let btnConfig;
    for (let i = 0; i < buttons.length; i++) {
      btnConfig = { ...this.getButtonConfig(buttons[i]) };
      if (btnConfig.type === 'component') this._createStateComponent(btnConfig);
      else this._createStateButton(btnConfig);
    }
  }

  _handleSettingsChanged() {
    this._checkHudIcons();
    this._updatePosition();
  }

  _checkHudIcons() {
    if (!this._hudConfig.icons) return;

    if (!this._hudIcons) {
      // hud icons
      this._hudIcons = new GameHudIcons(this.scene, this._hudConfig.icons);
      this._getButtonGroup('topright').addIconGroup(this._hudIcons);
    }

    this._hudIcons.updateDisplay(Object.keys(this._stateButtons), this.game.settings);
  }

  /** @private */
  _createStateButton(config) {
    // button already exists
    if (this._stateButtons[config.id]) return false;
    // initialize button
    let btn;
    if (config.classname) {
      btn = new config.classname(this.scene, config);
    } else if (config.buttonClass) {
      btn = new config.buttonClass(this.scene, config);
    } else {
      btn = new this.defaultButton(this.scene, config);
    }

    btn.on('click', this._handleButtonInteraction, this);
    this.scene.add.existing(btn);

    // add button to group if desired
    if (config.buttongroup) {
      this._addToButtonGroup(btn, config.buttongroup);
    }

    // register button
    this._stateButtons[config.id] = btn;
  }

  /** @private */
  _clearStateButtons() {
    Object.keys(this._stateButtons).forEach((key) => {
      this._stateButtons[key].destroy();
      this._stateButtons[key] = null;
    });
    Object.keys(this._buttonGroups).forEach((key) => {
      this._buttonGroups[key].clearButtons();
    });
    this._stateButtons = {};
  }

  /** @private */
  _handleButtonInteraction(btn) {
    const config = this.getButtonConfig(btn.id);
    if (config.event) this.events.emit(config.event);
    this.events.emit('BUTTON_SELECT', btn.id);
    this.events.emit(btn.id, btn.id);
  }

  /**
   * Get a button configuration object as set in hud config
   * @param {string} btnid
   * @returns button config if found
   */
  getButtonConfig(btnid) {
    let config = { ...this._hudConfig.buttons.find((b) => b.id === btnid) };
    while (config.extends) {
      const result = this._hudConfig.buttons.find((b) => b.id === config.extends);
      if (result) {
        config = { ...result, ...config };
        config.extends = result.extends;
      } else break;
    }

    return config;
  }

  /*
  =========================================================================================================
  BUTTON GROUPS
  =========================================================================================================
  */

  /** @private */
  _addToButtonGroup(btn, groupid) {
    const group = this._getButtonGroup(groupid);
    if (group) {
      group.addButton(btn);
    }
  }

  /** @private */
  _getButtonGroup(id) {
    if (!this._buttonGroups[id]) {
      this._createButtonGroup(id);
    }
    return this._buttonGroups[id];
  }

  /** @private */
  _createButtonGroup(id) {
    if (!this._buttonGroups[id]) {
      const groupdata = this._hudConfig.buttongroups[id] || DEFAULT_GROUPS[id];
      this._buttonGroups[id] = new GameHudButtonGroup(this.scene, this, {
        id,
        positiondata: { ...groupdata },
        viewport: this.game.viewportController,
      });
    }
  }

  // =============================================================================================
  // MODAL
  // =============================================================================================

  /**
   *
   * @param {bool} value Show or hide the Hudal modal background
   */
  showModal(value) {
    if (value) this._addModal();
    else this._removeModal();
  }

  /** @private */
  _addModal() {
    if (!this._modal) {
      const { colour = 0x000000, alpha = 0.8, imagekey, imageframe } = this._hudConfig.modal || {};
      if (imagekey) {
        this._modal = this.scene.add.image(0, 0, imagekey, imageframe).setAlpha(alpha);
        this._modal.setOrigin(0);
      } else if (colour) {
        const { width, height } = this.game.scale;
        this._modal = this.scene.add.rectangle(0, 0, width, height, colour, alpha);
        this._modal.setOrigin(0);
        this.scene.children.sendToBack(this._modal);
      }
    }
  }

  /** @private */
  _removeModal() {
    if (this._modal) {
      this._modal.destroy();
      this._modal = null;
    }
  }

  // =============================================================================================
  // RESPONSIVENESS
  // =============================================================================================

  /** @private */
  _updatePosition() {
    const keys = Object.keys(this._buttonGroups);
    keys.forEach((key) => {
      const bg = this._buttonGroups[key];
      if (bg.avoid.length == 0) {
        bg.updatePosition(this.game.viewportController);
      }
    });
    keys.forEach((key) => {
      const bg = this._buttonGroups[key];
      if (bg.avoid.length > 0) {
        bg.updatePosition(this.game.viewportController);
      }
    });
  }
}

export { GameHudPlugin };

const DEFAULT_GROUPS = {
  center: {
    anchor_group: { x: 0.5, y: 0.5 },
    anchor_screen: { x: 0.5, y: 0.5 },
    align_buttons: 'center',
  },
  bottom: {
    anchor_group: { x: 0.5, y: 1 },
    anchor_screen: { x: 0.5, y: 1 },
    align_buttons: 'center',
  },
  topright: {
    anchor_group: { x: 1, y: 0 },
    anchor_screen: { x: 1, y: 0 },
    align_buttons: 'center',
  },
  topleft: {
    anchor_group: { x: 0, y: 0 },
    anchor_screen: { x: 0, y: 0 },
    align_buttons: 'center',
  },
  bottomleft: {
    anchor_group: { x: 0, y: 1 },
    anchor_screen: { x: 0, y: 1 },
    align_buttons: 'center',
  },
  bottomright: {
    anchor_group: { x: 1, y: 1 },
    anchor_screen: { x: 1, y: 1 },
    align_buttons: 'center',
  },
};
